import { focusTask } from '../services/focusChooser';
import { useState } from 'react';
import type { FormEvent, MouseEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCircle2, Circle } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  GTD_STATUSES,
  GTD_STATUS_LABELS,
  containerOf,
  describeReminder,
  formatCalendarDate,
  isGtdStatus,
  mergeTaskDraft,
  taskDraft,
  taskDraftChanges,
  taskDraftChanged,
  upcomingReminder,
  wallTimeIn,
} from '@cerebro-diem/core';
import type { ContainerRow, FilingTarget, GtdStatus, Task, TaskStatus } from '@cerebro-diem/core';
import { errorMessage } from '../services/errors';
import { useProfile } from '../services/profile';
import { queryKeys } from '../services/queries';
import { TASK_STATUSES, TASK_STATUS_LABELS, dueLabel, saveTaskEdit, statusUpdate } from '../services/tasks';
import type { TaskUpdate } from '../services/tasks';
import ContainerChip, { chipClass } from './ContainerChip';
import ContainerPicker from './ContainerPicker';
import DeleteItem from './DeleteItem';
import DictationTextarea from './DictationTextarea';
import NoDeviceNote from './NoDeviceNote';
import { compactInput, inputClass, outlineButton, primaryButton } from './styles';
import { useUnsavedChanges } from './useUnsavedChanges';

/** A task edit: its columns, and its reminder when it changed (a wall time in the user's zone, or null to clear). */
interface TaskSave {
  update: TaskUpdate;
  reminder?: string | null;
}

/** A click that belongs to a control inside the row, not to the row itself. */
const ownClick = (e: MouseEvent) => e.stopPropagation();

/**
 * One task: mark it done, focus it for today, see where it lives, how it
 * flows (waiting, someday, context) and its reminder still to come. Tapping
 * the task opens it to edit, including moving it to another Project or Area,
 * setting its reminder, and deleting it. `showContainer` is false on its
 * container's own page, where the chip and the placement would repeat the
 * page. `onDeleted` runs once it is deleted.
 */
export default function TaskItem({
  task,
  today,
  containers,
  showContainer = true,
  serves,
  onDeleted,
}: {
  task: Task;
  today: string;
  containers: readonly ContainerRow[];
  showContainer?: boolean;
  /** The outcome this task serves, where the list it is in is ordered by that (Today's lead, CER-181). */
  serves?: string;
  onDeleted?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    // One database call: a refused reminder saves nothing of the edit.
    mutationFn: ({ update, reminder }: TaskSave) => saveTaskEdit(task.id, update, reminder),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      await queryClient.invalidateQueries({ queryKey: queryKeys.searches });
      setEditing(false);
    },
  });
  // Focusing goes through focusTask: a full day asks which focused task makes room (CER-135).
  const focusMutation = useMutation({
    mutationFn: (date: string | null) => focusTask(task.id, task.name, date),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      await queryClient.invalidateQueries({ queryKey: queryKeys.searches });
    },
  });
  const containersById = new Map(containers.map((c) => [c.id, c]));
  // A reminder reads in the user's zone; without one there is none to show.
  const profile = useProfile();
  const zone = profile.data?.timezone ?? null;
  const now = new Date();
  const reminderAt = upcomingReminder(task, now);
  const reminderText = reminderAt && zone ? describeReminder(reminderAt, zone, now).text : null;

  const isDone = task.status === 'done';
  const overdue = !isDone && task.due_date !== null && task.due_date < today;
  const dueToday = !isDone && task.due_date === today;
  const inFocus = task.focus_date === today;
  const error = mutation.isError ? errorMessage(mutation.error) : focusMutation.isError ? errorMessage(focusMutation.error) : null;

  if (editing) {
    return (
      <TaskEditor
        task={task}
        containers={containers}
        showPlacement={showContainer}
        saving={mutation.isPending}
        error={error}
        onSave={(save) => mutation.mutate(save)}
        onCancel={() => {
          mutation.reset();
          setEditing(false);
        }}
        onDeleted={onDeleted}
      />
    );
  }

  const open = () => {
    if (mutation.isPending) return;
    mutation.reset();
    setEditing(true);
  };

  return (
    // The whole row opens the task; its name is the button that does it from the keyboard.
    // On a phone the focus button wraps under the task, so the name keeps the row's width; from sm it sits at the right.
    <div
      onClick={open}
      className={`rounded-xl p-3 bg-surface-variant/30 hover:bg-surface-variant/50 transition-colors cursor-pointer flex flex-wrap items-start gap-x-3 gap-y-2 ${
        overdue ? 'border-l-4 border-error' : ''
      }`}
    >
      {/* A 44px target around the 24px circle; the negative margin keeps the row laid out as for the circle alone. */}
      <button
        type="button"
        onClick={(e) => {
          ownClick(e);
          mutation.mutate({ update: statusUpdate(isDone ? 'pending' : 'done') });
        }}
        disabled={mutation.isPending}
        aria-label={isDone ? `Mark "${task.name}" not done` : `Mark "${task.name}" done`}
        className="flex-shrink-0 -mx-2.5 -mb-2.5 -mt-2 p-2.5 rounded-full"
      >
        {isDone ? (
          <CheckCircle2 className="w-6 h-6 text-success" />
        ) : (
          <Circle className="w-6 h-6 text-on-surface-variant hover:text-primary transition-colors" />
        )}
      </button>

      {/* Everything but the circle (1.5rem) and the gap (0.75rem) on a phone; what is left beside the button from sm. */}
      <div className="min-w-0 grow basis-[calc(100%-2.25rem)] sm:basis-0 space-y-1">
        {/* No handler of its own: its click (mouse, Enter, Space) reaches the row's. */}
        <button
          type="button"
          className={`block w-full text-left font-medium break-words rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
            isDone ? 'line-through text-on-surface-variant' : ''
          }`}
        >
          {task.name}
        </button>
        <div className="flex flex-wrap items-center gap-1.5">
          {showContainer && (
            // The chip is a link to the container; following it does not also open the task.
            <span onClick={ownClick}>
              <ContainerChip item={task} containersById={containersById} />
            </span>
          )}
          {task.gtd_status !== 'next' && (
            <span className={`${chipClass} bg-tertiary/10 text-tertiary`}>{GTD_STATUS_LABELS[task.gtd_status]}</span>
          )}
          {task.gtd_status === 'next' && task.context && (
            <span className={`${chipClass} bg-surface-variant text-on-surface-variant`}>@{task.context}</span>
          )}
          {serves && <span className={`${chipClass} bg-secondary-container text-secondary`}>For: {serves}</span>}
          {inFocus && <span className={`${chipClass} bg-primary-container text-primary`}>Today's focus</span>}
          {task.importance === 'high' && <span className={`${chipClass} bg-error/10 text-error`}>Important</span>}
        </div>
        {task.due_date && (
          <p className={`text-xs ${overdue ? 'text-error font-medium' : dueToday ? 'text-tertiary font-medium' : 'text-on-surface-variant'}`}>
            {isDone ? `Due ${formatCalendarDate(task.due_date)}` : dueLabel(task.due_date, today)}
          </p>
        )}
        {reminderText && (
          <p className="text-xs text-primary inline-flex items-center gap-1">
            <Bell className="w-3 h-3" aria-hidden="true" />
            Reminder: {reminderText}
          </p>
        )}
        {task.notes && <p className="text-sm text-on-surface-variant whitespace-pre-wrap line-clamp-3">{task.notes}</p>}
        {error && <p className="text-xs text-error">Save not confirmed: {error}</p>}
      </div>

      {!isDone && (
        <button
          type="button"
          onClick={(e) => {
            ownClick(e);
            focusMutation.mutate(inFocus ? null : today);
          }}
          disabled={mutation.isPending || focusMutation.isPending}
          className={`${outlineButton} flex-shrink-0 ml-9 sm:ml-0`}
        >
          {inFocus ? 'Remove from focus' : 'Focus today'}
        </button>
      )}
    </div>
  );
}

function TaskEditor({
  task,
  containers,
  showPlacement,
  saving,
  error,
  onSave,
  onCancel,
  onDeleted,
}: {
  task: Task;
  containers: readonly ContainerRow[];
  /** False on the task's container page: the placement stays behind "Move…" there. */
  showPlacement: boolean;
  saving: boolean;
  error: string | null;
  onSave: (save: TaskSave) => void;
  onCancel: () => void;
  onDeleted?: () => void;
}) {
  const profile = useProfile();
  const [sourceTask, setSourceTask] = useState(task);
  const current = containerOf(sourceTask);
  const [name, setName] = useState(task.name);
  const [dueDate, setDueDate] = useState(task.due_date ?? '');
  const [notes, setNotes] = useState(task.notes ?? '');
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [gtdStatus, setGtdStatus] = useState<GtdStatus>(task.gtd_status);
  const [context, setContext] = useState(task.context ?? '');
  const [target, setTarget] = useState<FilingTarget | null>(current);
  const [moving, setMoving] = useState(false);
  const [validation, setValidation] = useState<string | null>(null);
  // Says what the picker chooses; the chosen container's kind is the icon beside it.
  const placement = 'Project or area';
  // The reminder, read in the user's zone; it is sent only when changed here.
  const zone = profile.data?.timezone ?? null;
  const savedReminder = sourceTask.remind_at && zone ? wallTimeIn(zone, new Date(sourceTask.remind_at)) : '';
  const [reminderEdit, setReminderEdit] = useState<{ date: string; time: string } | null>(null);
  const reminderDate = reminderEdit?.date ?? savedReminder.slice(0, 10);
  const reminderTime = reminderEdit?.time ?? savedReminder.slice(11, 16);
  // Leaving with any field changed asks first; Save and Delete leave without asking.
  const changed = taskDraftChanged(taskDraft(sourceTask, savedReminder), {
    name,
    notes,
    due_date: dueDate,
    status,
    gtd_status: gtdStatus,
    context,
    target,
    reminder: reminderDate || reminderTime ? `${reminderDate}T${reminderTime}` : '',
  });
  if (task !== sourceTask) {
    const freshReminder = task.remind_at && zone ? wallTimeIn(zone, new Date(task.remind_at)) : '';
    const merged = mergeTaskDraft(taskDraft(sourceTask, savedReminder), {
      name, notes, due_date: dueDate, status, gtd_status: gtdStatus, context, target,
      reminder: reminderDate || reminderTime ? `${reminderDate}T${reminderTime}` : '',
    }, taskDraft(task, freshReminder));
    setSourceTask(task);
    setName(merged.name);
    setDueDate(merged.due_date);
    setNotes(merged.notes);
    setStatus(merged.status);
    setGtdStatus(merged.gtd_status);
    setContext(merged.context);
    setTarget(merged.target);
    setReminderEdit(merged.reminder === freshReminder ? null : { date: merged.reminder.slice(0, 10), time: merged.reminder.slice(11, 16) });
  }
  const { confirmLeave, leaveWithoutAsking } = useUnsavedChanges(changed);
  // A context the user has since removed from their list still shows, so saving does not drop it silently.
  const contexts = [...(profile.data?.gtd_contexts ?? [])];
  if (sourceTask.context && !contexts.includes(sourceTask.context)) contexts.push(sourceTask.context);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setValidation('A task needs a name.');
      return;
    }
    if (!target || 'newName' in target || target.kind === 'resource') {
      setValidation('Choose the Project or Area this task lives in.');
      return;
    }
    if (reminderEdit && Boolean(reminderEdit.date) !== Boolean(reminderEdit.time)) {
      setValidation('A reminder needs both a date and a time.');
      return;
    }
    setValidation(null);
    const changes = taskDraftChanges(taskDraft(sourceTask, savedReminder), {
      name, notes, due_date: dueDate, status, gtd_status: gtdStatus, context, target,
      reminder: reminderDate || reminderTime ? `${reminderDate}T${reminderTime}` : '',
    });
    onSave({
      reminder: changes.reminder,
      update: changes.update,
    });
  };

  return (
    <form onSubmit={submit} className="rounded-xl p-3 bg-surface-variant/30 space-y-3">
      <label className="block space-y-1">
        <span className="text-xs font-medium text-on-surface-variant">Name</span>
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
      </label>

      {showPlacement || moving ? (
        <label className="block space-y-1">
          <span className="text-xs font-medium text-on-surface-variant">{placement}</span>
          <ContainerPicker containers={containers} kinds={['project', 'area']} value={target} onChange={setTarget} label={placement} />
        </label>
      ) : (
        <button type="button" onClick={() => setMoving(true)} className={outlineButton}>
          Move to another project or area
        </button>
      )}

      {/*
        One row when space permits; whole fields wrap on narrower screens.
        The reminder date/time stay together, with 16px touch inputs so iOS does not zoom.
      */}
      <div role="group" aria-label="Task options" className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <label className="block space-y-1">
          <span className="block text-xs font-medium text-on-surface-variant">Status</span>
          <select
            aria-label="Status"
            className={`${compactInput} block`}
            value={status}
            onChange={(e) => {
              const next = TASK_STATUSES.find((option) => option === e.target.value);
              if (next) setStatus(next);
            }}
          >
            {TASK_STATUSES.map((option) => (
              <option key={option} value={option}>
                {TASK_STATUS_LABELS[option]}
              </option>
            ))}
          </select>
        </label>

        <label className="block space-y-1">
          <span className="block text-xs font-medium text-on-surface-variant">GTD</span>
          <select
            className={`${compactInput} block`}
            value={gtdStatus}
            onChange={(e) => {
              if (isGtdStatus(e.target.value)) setGtdStatus(e.target.value);
            }}
          >
            {GTD_STATUSES.map((option) => (
              <option key={option} value={option}>
                {GTD_STATUS_LABELS[option]}
              </option>
            ))}
          </select>
        </label>

        {gtdStatus === 'next' && (
          <label className="block space-y-1">
            <span className="block text-xs font-medium text-on-surface-variant">Context</span>
            <select className={`${compactInput} block`} value={context} onChange={(e) => setContext(e.target.value)}>
              <option value="">No context</option>
              {contexts.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="space-y-1">
          <span className="block text-xs font-medium text-on-surface-variant">Due date</span>
          <div className="flex items-center gap-2">
            <input type="date" aria-label="Due date" className={compactInput} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            {dueDate && (
              <button type="button" onClick={() => setDueDate('')} className={outlineButton}>
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="min-w-0 max-w-full space-y-1">
          <span className="block text-xs font-medium text-on-surface-variant">Reminder{zone ? ` (${zone})` : ''}</span>
          {zone ? (
            <div className="flex flex-wrap items-center gap-2">
              {/* The date and time stay together, shrinking to the available width on small phones. */}
              <div className="flex min-w-0 max-w-full items-center gap-2">
                <input
                  type="date"
                  aria-label="Reminder date"
                  className={`${compactInput} min-w-0`}
                  value={reminderDate}
                  onChange={(e) => setReminderEdit({ date: e.target.value, time: reminderTime })}
                />
                <input
                  type="time"
                  aria-label="Reminder time"
                  className={`${compactInput} min-w-0`}
                  value={reminderTime}
                  onChange={(e) => setReminderEdit({ date: reminderDate, time: e.target.value })}
                />
              </div>
              {(reminderDate || reminderTime) && (
                <button type="button" onClick={() => setReminderEdit({ date: '', time: '' })} className={outlineButton}>
                  Clear
                </button>
              )}
              {reminderDate && reminderTime && <NoDeviceNote />}
            </div>
          ) : profile.isPending ? (
            <p className="text-sm text-on-surface-variant">Loading your time zone…</p>
          ) : (
            <p className="text-sm text-on-surface-variant">
              Reminders need your time zone:{' '}
              <Link to="/settings" className="text-primary underline">
                set it in Settings
              </Link>
              .
            </p>
          )}
        </div>
      </div>

      <DictationTextarea label="Notes" rows={3} value={notes} onChange={setNotes} />

      {profile.isError && <p className="text-sm text-error">Could not load your contexts: {errorMessage(profile.error)}</p>}
      {validation && <p className="text-sm text-error">{validation}</p>}
      {error && <p className="text-sm text-error">Save not confirmed: {error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={saving} className={primaryButton}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={() => confirmLeave(onCancel)} disabled={saving} className={outlineButton}>
          Cancel
        </button>
        {/* The task is gone, so its edits are too: nothing to ask about. */}
        <DeleteItem kind="task" id={task.id} name={task.name} onDeleted={() => leaveWithoutAsking(() => onDeleted?.())} />
      </div>
    </form>
  );
}
